import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { sitesApi } from "../lib/sites";
import { groupsApi } from "../lib/groups";

export type UsageAccessScope = {
  /** True once role-specific access data has loaded (or is not needed). */
  ready: boolean;
  role: "super_user" | "site_admin" | "end_user" | undefined;
  /** Site IDs the viewer may see. Undefined = unrestricted (super user). */
  allowedSiteIds: Set<string> | undefined;
  /** Group IDs the viewer may see. Undefined = unrestricted (super user / site admin). */
  allowedGroupIds: Set<string> | undefined;
};

/**
 * Access scope for usage views.
 * - Super user: unrestricted
 * - Site admin: assigned sites (and thus their organisations via those sites)
 * - End user: groups they belong to
 */
export function useUsageAccessScope(): UsageAccessScope {
  const { user } = useAuth();
  const role = user?.role;

  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ["sites", "usage-access", user?.id],
    queryFn: () => sitesApi.getAll(),
    enabled: !!user && role === "site_admin",
    staleTime: 60_000,
  });

  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ["groups", "usage-access", user?.id],
    queryFn: () => groupsApi.getAll(),
    enabled: !!user && role === "end_user",
    staleTime: 60_000,
  });

  return useMemo(() => {
    if (!user || !role) {
      return {
        ready: false,
        role: undefined,
        allowedSiteIds: new Set(),
        allowedGroupIds: new Set(),
      };
    }

    if (role === "super_user") {
      return {
        ready: true,
        role,
        allowedSiteIds: undefined,
        allowedGroupIds: undefined,
      };
    }

    if (role === "site_admin") {
      return {
        ready: !sitesLoading,
        role,
        allowedSiteIds: new Set((sites ?? []).map((s) => s.id)),
        allowedGroupIds: undefined,
      };
    }

    // end_user
    return {
      ready: !groupsLoading,
      role,
      allowedSiteIds: new Set((groups ?? []).map((g) => g.siteId)),
      allowedGroupIds: new Set((groups ?? []).map((g) => g.id)),
    };
  }, [user, role, sites, sitesLoading, groups, groupsLoading]);
}

/**
 * Whether a usage row is visible under the current access scope.
 */
export function isUsageEventInScope(
  event: { siteId?: string | null; groupId?: string | null },
  scope: UsageAccessScope,
): boolean {
  if (!scope.ready || !scope.role) return false;
  if (scope.role === "super_user") return true;
  if (scope.allowedSiteIds && event.siteId && !scope.allowedSiteIds.has(event.siteId)) {
    return false;
  }
  if (scope.allowedGroupIds) {
    if (!event.groupId || !scope.allowedGroupIds.has(event.groupId)) {
      return false;
    }
  }
  return true;
}
